<template>
  <a-drawer :title="title" width="60%" :visible="visible" :body-style="{ paddingBottom: '80px' }" @close="onClose">
    <a-spin :spinning="loading">
      <div class="drawer-content">
        <!-- 基本信息 -->
        <a-descriptions title="基本信息" :column="2" bordered>
          <a-descriptions-item label="标题">{{ detailData.couponName || '-' }}</a-descriptions-item>
          <a-descriptions-item label="副标题">{{ detailData.subTitle || '-' }}</a-descriptions-item>
          <a-descriptions-item label="标题图" span="2">
            <img
              :src="getImageUrl(detailData.fileUrl)"
              alt="标题图"
              style="object-fit: cover; border: 1px solid #d9d9d9; border-radius: 4px"
              @error="handleImageError"
            />
          </a-descriptions-item>
          <a-descriptions-item label="类型">
            <a-tag :color="getTypeColor(detailData.couponType)">
              {{ couponTypeMap[detailData.couponType] || '-' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="模块">
            <a-tag>{{ whTypeMap[detailData.whType] || '-' }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="获取方式" span>
            <a-tag :color="getWayColor(detailData.getWay)">
              {{ getWayMap[detailData.getWay] || '-' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="适用类型">
            {{ suitableProdTypeMap[detailData.suitableProdType] || '-' }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 优惠信息 -->
        <a-descriptions title="优惠信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="减免金额">{{ detailData.reduceAmount || 0 }} 元</a-descriptions-item>
          <a-descriptions-item label="折扣额度">
            {{ detailData.couponDiscount ? (detailData.couponDiscount * 10).toFixed(1) + ' 折' : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="使用条件">满 {{ detailData.cashCondition || 0 }} 元可用</a-descriptions-item>
        </a-descriptions>

        <!-- 库存信息 -->
        <a-descriptions title="库存信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="原始库存">
            {{ detailData.sourceStock === 0 ? '无限制' : detailData.sourceStock || 0 }}
          </a-descriptions-item>
          <a-descriptions-item label="当前库存">
            {{ detailData.sourceStock === 0 ? '无限制' : detailData.stocks || 0 }}
          </a-descriptions-item>
          <a-descriptions-item label="已领取">{{ detailData.receiveNum || 0 }}</a-descriptions-item>
          <a-descriptions-item label="用户领取上限">
            {{ detailData.limitNum === 0 ? '无限制领取' : detailData.limitNum + ' 张' }}
          </a-descriptions-item>
          <a-descriptions-item label="领取间隔">
            {{ getLimitCycleText(detailData.limitCycle) }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 生效时间 -->
        <a-descriptions title="生效时间" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="生效方式">
            <a-tag :color="detailData.validTimeType === 1 ? 'blue' : 'green'">
              {{ validTimeTypeMap[detailData.validTimeType] || '-' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item v-if="detailData.validTimeType === 1" label="固定时段">
            {{ detailData.startTime }} ~ {{ detailData.endTime }}
          </a-descriptions-item>
          <a-descriptions-item v-else label="有效天数"> {{ detailData.validDays || 0 }} 天 </a-descriptions-item>
          <a-descriptions-item label="领券后生效天数">
            {{ detailData.afterReceiveDays ? detailData.afterReceiveDays + ' 天' : '立即生效' }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 状态信息 -->
        <a-descriptions title="状态信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="过期状态">
            <a-tag :color="detailData.overdueStatus === 1 ? 'green' : 'red'">
              {{ overdueStatusMap[detailData.overdueStatus] || '-' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="投放状态">
            <a-tag :color="getPutonStatusColor(detailData.putonStatus)">
              {{ putonStatusMap[detailData.putonStatus] || '-' }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <!-- 审核信息 -->
        <a-descriptions title="审核信息" :column="2" bordered style="margin-top: 16px" v-if="showAuditInfo">
          <a-descriptions-item label="审核人">{{ detailData.auditingUserName || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核时间">{{ detailData.auditingTime || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核说明" :span="2">
            {{ detailData.auditingVal || '-' }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 其他信息 -->
        <a-descriptions title="其他信息" :column="1" bordered style="margin-top: 16px">
          <a-descriptions-item label="备注">{{ detailData.remark || '-' }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ detailData.createTime || '-' }}</a-descriptions-item>
        </a-descriptions>

        <!-- 适用商品信息 -->
        <a-card title="适用商品范围" style="margin-top: 16px" v-if="showProductInfo">
          <a-alert :message="getSuitableProductMessage()" type="info" show-icon />
          <div v-if="detailData.comboProds && detailData.comboProds.length" style="margin-top: 12px">
            <div style="font-weight: 500; margin-bottom: 8px">适用套餐：</div>
            <a-tag
              v-for="(combo, index) in detailData.comboProds"
              :key="combo.prodId || index"
              style="margin-bottom: 4px"
            >
              {{ combo.prodName || `套餐 ${combo.prodId}` }}
            </a-tag>
          </div>
          <div v-if="detailData.siteIds && detailData.siteIds.length" style="margin-top: 12px">
            <div style="font-weight: 500; margin-bottom: 8px">适用场地：</div>
            <a-tag v-for="siteId in detailData.siteIds" :key="siteId" style="margin-bottom: 4px">
              场地 {{ siteId }}
            </a-tag>
          </div>
        </a-card>
      </div>
    </a-spin>

    <div class="drawer-footer">
      <a-button @click="onClose">关闭</a-button>
    </div>
  </a-drawer>
</template>

<script>
import { getCouponDetail } from '@/api/coupon/controller/index.js'

export default {
  name: 'CouponDetailDrawer',
  data() {
    return {
      visible: false,
      loading: false,
      title: '优惠券详情',
      detailData: {},
      // 默认图片
      defaultImage: 'http://hoy-test-1308702491.cos.ap-beijing.myqcloud.com/hoy/oth/IMG_20251121_211227__342045.png',

      // 映射配置
      couponTypeMap: {
        10: '代金券',
        20: '折扣券',
        30: '免单券',
      },
      whTypeMap: {
        0: '通用',
        10: '商城',
        20: '维修',
        30: '全景套餐',
      },
      getWayMap: {
        0: '客户领取',
        10: '平台发放',
        20: '激活码领取',
        30: '二维码扫码',
        40: '核销优惠券',
      },
      suitableProdTypeMap: {
        0: '全部商品参与',
        10: '指定商品参与',
        20: '指定商品不参与',
      },
      validTimeTypeMap: {
        1: '固定时间',
        2: '领取后生效',
      },
      overdueStatusMap: {
        0: '已过期',
        1: '未过期',
      },
      putonStatusMap: {
        0: '取消投放',
        10: '等待投放',
        20: '投放中',
        30: '违规下架',
        40: '等待审核',
        50: '审核不通过',
      },
    }
  },
  computed: {
    showAuditInfo() {
      return this.detailData.auditingUserName || this.detailData.auditingTime || this.detailData.auditingVal
    },
    showProductInfo() {
      return (
        this.detailData.suitableProdType !== 0 ||
        (this.detailData.comboProds && this.detailData.comboProds.length) ||
        (this.detailData.siteIds && this.detailData.siteIds.length)
      )
    },
  },
  methods: {
    async open(record) {
      this.visible = true
      this.title = `优惠券详情 - ${record.couponName}`
      await this.loadDetail(record.id)
    },
    onClose() {
      this.visible = false
      this.detailData = {}
    },
    async loadDetail(id) {
      this.loading = true
      try {
        const response = await getCouponDetail({ id })
        if (response.code === '20001') {
          this.detailData = response.data || {}
        } else {
          this.$message.error(response.msg || '获取优惠券详情失败')
        }
      } catch (error) {
        console.error('加载优惠券详情失败:', error)
        this.$message.error('加载优惠券详情失败')
      } finally {
        this.loading = false
      }
    },
    getTypeColor(type) {
      const colorMap = {
        10: 'blue', // 代金券
        20: 'green', // 折扣券
        30: 'orange', // 免单券
      }
      return colorMap[type] || 'default'
    },
    getWayColor(way) {
      const colorMap = {
        0: 'blue', // 客户领取
        10: 'green', // 平台发放
        20: 'orange', // 激活码领取
        30: 'purple', // 二维码扫码
        40: 'cyan', // 核销优惠券
      }
      return colorMap[way] || 'default'
    },
    getPutonStatusColor(status) {
      const colorMap = {
        0: 'red', // 取消投放
        10: 'orange', // 等待投放
        20: 'green', // 投放中
        30: 'red', // 违规下架
        40: 'blue', // 等待审核
        50: 'red', // 审核不通过
      }
      return colorMap[status] || 'default'
    },
    getLimitCycleText(cycle) {
      if (cycle === -1) return '周间隔'
      if (cycle === -2) return '月间隔'
      if (cycle && cycle > 0) return `${cycle} 天`
      return '-'
    },
    getSuitableProductMessage() {
      const type = this.detailData.suitableProdType
      switch (type) {
        case 0:
          return '全部商品均可使用此优惠券'
        case 10:
          return '仅指定商品可使用此优惠券'
        case 20:
          return '指定商品不可使用此优惠券'
        default:
          return '商品适用范围未定义'
      }
    },
    getImageUrl(fileUrl) {
      // 如果没有图片URL，返回默认图片
      if (!fileUrl || fileUrl.trim() === '') {
        return this.defaultImage
      }
      return fileUrl
    },
    handleImageError(event) {
      // 图片加载失败时显示默认图片
      event.target.src = this.defaultImage
    },
  },
}
</script>

<style scoped>
.drawer-content {
  padding: 0;
}

.drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #e9e9e9;
  padding: 10px 16px;
  background: #fff;
  text-align: right;
  z-index: 1;
}

.ant-descriptions {
  margin-bottom: 0;
}

.ant-card {
  margin-bottom: 16px;
}

.ant-card:last-child {
  margin-bottom: 0;
}

:deep(.ant-descriptions-item-label) {
  width: 150px;
  text-align: right;
}
</style>
